import React from 'react';
import { useConfigStore } from '@stores/configStore';
import { Globe, AlertCircle, CheckCircle } from 'lucide-react';

interface APIConfigFormProps {
  onTestConnection?: () => Promise<void>;
  testResult?: { success: boolean; message: string };
}

export const APIConfigForm: React.FC<APIConfigFormProps> = ({ onTestConnection, testResult }) => {
  const { config, updateAPI } = useConfigStore();
  const api = config.api;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Globe className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold">API Configuration</h3>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Base URL</label>
        <input
          type="text"
          value={api.baseUrl}
          onChange={(e) => updateAPI({ baseUrl: e.target.value })}
          placeholder="http://localhost:3000"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mt-1">The backend API server URL</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Timeout (seconds)</label>
          <input
            type="number"
            value={api.timeout}
            onChange={(e) => updateAPI({ timeout: parseInt(e.target.value) })}
            min="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Retry Attempts</label>
          <input
            type="number"
            value={api.retryAttempts}
            onChange={(e) => updateAPI({ retryAttempts: parseInt(e.target.value) })}
            min="0"
            max="10"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Retry Delay (seconds)</label>
          <input
            type="number"
            value={api.retryDelay}
            onChange={(e) => updateAPI({ retryDelay: parseInt(e.target.value) })}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium text-gray-900 mb-3">CORS Settings</h4>
        <div className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            id="cors"
            checked={api.enableCORS}
            onChange={(e) => updateAPI({ enableCORS: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <label htmlFor="cors" className="text-sm font-medium text-gray-700">
            Enable CORS
          </label>
        </div>
        {api.enableCORS && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Origins (comma-separated)</label>
            <textarea
              value={api.corsOrigins.join('\n')}
              onChange={(e) => updateAPI({ corsOrigins: e.target.value.split('\n').filter((o) => o.trim()) })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium text-gray-900 mb-3">Rate Limiting</h4>
        <div className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            id="rateLimit"
            checked={api.rateLimitEnabled}
            onChange={(e) => updateAPI({ rateLimitEnabled: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <label htmlFor="rateLimit" className="text-sm font-medium text-gray-700">
            Enable Rate Limiting
          </label>
        </div>
        {api.rateLimitEnabled && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Requests per Window</label>
              <input
                type="number"
                value={api.rateLimitRequests}
                onChange={(e) => updateAPI({ rateLimitRequests: parseInt(e.target.value) })}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Window Size (seconds)</label>
              <input
                type="number"
                value={api.rateLimitWindow}
                onChange={(e) => updateAPI({ rateLimitWindow: parseInt(e.target.value) })}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>

      {onTestConnection && (
        <button
          onClick={onTestConnection}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Test Connection
        </button>
      )}

      {testResult && (
        <div className={`p-4 rounded-lg flex items-start gap-3 ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
          {testResult.success ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={testResult.success ? 'text-green-800' : 'text-red-800'}>{testResult.message}</p>
        </div>
      )}
    </div>
  );
};
